import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Button, ButtonGroup, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, } from '@material-ui/core';
import { FormattedMessage } from 'react-intl';
import { ActionRow, Dialog, Form, OccupyFreeSpace, SaveButton, SearchField, SimpleDialog, Tooltip, useCopyText, useWindowHotkey } from '../primitives';
import { FormControlsForFields } from '../schemed';
import { LocalizationMessagesEdit, Mode } from './useLocalizationMessagesEdit';
import { Add, Warning } from '@material-ui/icons';
import { Checkbox } from '../schemed/Checkbox';
import { useSaveable } from '../primitives/useSaveable';


const JsonEditor = styled.textarea`
    background: #333355;
    color: #fefeee;
    border-radius: 4px;
    min-height: 100%;
    font-family: monospace;
`;


interface Props {
    data: LocalizationMessagesEdit;
}

export const LocalizationMessages = (props: Props) => {
    const {
        mode,
        setMode,

        selectedLanguage,
        setSelectedLanguage,
        availableLanguages,
        addLanguage,

        messages,
        messagesKeys,
        updateMessage,
        addMessage,
        showEmptyOnly,
        setShowEmptyOnly,
        filter,
        setFilter,
        
        updateJson,
        messagesJson,
        isJsonError,
        
        hasChanges,
        save,

        pullMessagesFromOtherLanguage,
    } = props.data;

    const copyText = useCopyText();

    const [newLanguage, setNewLanguage] = useState<{ lang: string } | null>(null);

    const startAddNewLanguage = () => {
        setNewLanguage({ lang: "" });
    }

    const [newKey, setNewKey] = useState<string | null>(null);
    const startAddNewKey = () => {
        setNewKey("");
    }

    const [isPullMessagesOpen, setIsPullMessagesOpen] = useState<boolean>(false);

    
    const addNewIfInStrings = () => {
        if(mode === "strings") {
            startAddNewKey();
        }
    }
    
    useSaveable(props.data);
    useWindowHotkey("alt+n", addNewIfInStrings);

    return (
        <Form
            fitFullHeight
            formBodyProps={{
                style: { height: "100%" },
            }}
            title={<FormattedMessage id="localization.messages.title" />}
            headerItems={<>
                <ButtonGroup key="mode" size="small" color="primary" disabled={hasChanges}>
                    {["strings", "json"].map(m => (
                        <Button
                            key={m}
                            variant={m === mode ? "contained" : "outlined"}
                            onClick={() => setMode(m as Mode)}>
                                <FormattedMessage id={`localization.messages.mode.${m}`} />
                        </Button>
                    ))}
                </ButtonGroup>
                <OccupyFreeSpace />

                <Tooltip text_id="localization.messages.pull_messages_from_other_lang_hint">
                    <Button
                        key="pull"
                        color="primary"
                        disabled={hasChanges}
                        onClick={() => setIsPullMessagesOpen(true)}>
                            <FormattedMessage id="localization.messages.pull_messages_from_other_lang" />
                    </Button>
                </Tooltip>

                {isJsonError && (
                    <Tooltip text_id="localization.messages.json_error">
                        <Warning htmlColor="#cc0000" />
                    </Tooltip>
                )}

                <ButtonGroup key="lang" size="small" color="primary">
                    {availableLanguages.map(l => (
                        <Button
                            key={l}
                            variant={l === selectedLanguage ? "contained" : "outlined"}
                            onClick={() => setSelectedLanguage(l)}>
                                {l}
                        </Button>
                    ))}
                    <Button
                        key="new"
                        variant="outlined"
                        onClick={startAddNewLanguage}>
                            + <FormattedMessage id="localization.messages.add_language" />
                    </Button>
                </ButtonGroup>

                {hasChanges && <SaveButton action={save} />}
            </>}
        >
            {mode === "strings" && (
                <>
                    <ActionRow alignItems="end">
                        <IconButton size="small" onClick={startAddNewKey}>
                            <Add />
                        </IconButton>

                        <OccupyFreeSpace />
                        <Checkbox
                            field="showEmptyOnly"
                            row={{ showEmptyOnly }}
                            onChange={(_,c) => setShowEmptyOnly(c.showEmptyOnly)}
                            schema={{ label_id: "localization.messages.show_empty_only" }}
                            extraProps={{ controlProps: { style: { padding: "0 9px" }}}}
                            />

                        <SearchField
                            filter={filter}
                            setFilter={setFilter}
                            noButton
                            autoFocus
                            />
                    </ActionRow>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell key="k"><FormattedMessage id="localization.messages.table.key" /></TableCell>
                                    <TableCell key="v"><FormattedMessage id="localization.messages.table.message" /></TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {messagesKeys.map(k => (
                                    <TableRow key={k}>
                                        <TableCell key="k" onClick={() => copyText(k)}>{k}</TableCell>
                                        <TableCell key="v">
                                            <TextField
                                                style={{ width: "100%" }}
                                                multiline
                                                value={messages[k]}
                                                onChange={e => updateMessage(k, e.target.value)}
                                                />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {mode === "json" && <JsonEditor
                value={messagesJson}
                onChange={e => updateJson(e.target.value)}
                
                />}

            <SimpleDialog
                isOpen={!!newLanguage}
                close={() => setNewLanguage(null)}
                dialogTitle={<FormattedMessage id="localization.messages.add_language" />}
                noFullscreen
                save={() => {
                    if(newLanguage?.lang) {
                        addLanguage(newLanguage.lang);
                        setNewLanguage(null);
                    }
                }}>
                <FormControlsForFields
                    data={newLanguage}
                    fields={[["lang", { controlProps: { autoFocus: true }}]]}
                    schema={{ lang: { label_id: "localization.messages.language_code" }}}
                    onChange={(o,c) => setNewLanguage({ ...o, ...c })}
                    />
            </SimpleDialog>

            <SimpleDialog
                isOpen={newKey !== null}
                close={() => setNewKey(null)}
                dialogTitle={<FormattedMessage id="localization.messages.add_message" />}
                noFullscreen
                save={() => {
                    if(newKey && !messages[newKey]) {
                        addMessage(newKey);                        
                        setNewKey(null);
                    }
                }}>
                <FormControlsForFields
                    data={{ k: newKey }}
                    fields={[["k", { controlProps: { autoFocus: true }}]]}
                    schema={{ k: { label_id: "localization.messages.table.key" }}}
                    onChange={(o,c) => setNewKey(c.k)}
                    />
            </SimpleDialog>


            <Dialog
                isOpen={!!isPullMessagesOpen}
                close={() => setIsPullMessagesOpen(false)}
                dialogTitle={<FormattedMessage id="localization.messages.pull_messages_from_other_lang" />}
                noFullscreen>
                    <Typography><FormattedMessage id="localization.messages.pull_messages_from_other_lang_select_lang" /></Typography>
                    <ButtonGroup color="primary">
                        {availableLanguages.filter(l => l !== selectedLanguage).map(l => (
                            <Button
                                key={l}
                                variant="outlined"
                                onClick={() => { pullMessagesFromOtherLanguage(l); setIsPullMessagesOpen(false); }}>
                                    {l}
                            </Button>
                        ))}
                    </ButtonGroup>
            </Dialog>
        </Form>
    );
}
